import { Bot } from "lucide-react";
import * as React from "react";

import { useNavigation } from "@/providers";
import { DeviceLink } from "@/shared/components/DeviceLink";
import {
  auditFacts,
  EntityHeader,
  type EntityHeaderFact,
  EntityName,
  InlineEdit,
} from "@/shared/components/EntityPage";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type DatasetRecord } from "@/shared/domain/datasets/DatasetRecord";
import { useDataset, useUpdateDataset } from "@/shared/domain/datasets/hooks";

interface DatasetHeaderProps {
  datasetId: string;
}

/**
 * The header of a dataset page: name, then who created it and when, the
 * device it came from when it has one, then the description and tags (each
 * one searches for datasets with it). A skeleton stands in for all of it
 * until the dataset loads.
 *
 * Editing is offered to everyone who can see the dataset: an
 * editor of a dataset shared from another org may change it, and the API
 * refuses a change the viewer may not make.
 */
export const DatasetHeader: React.FC<DatasetHeaderProps> = ({ datasetId }) => {
  const { goto } = useNavigation();
  const datasetQuery = useDataset(datasetId, { throwOnError: true });
  const dataset = datasetQuery.data;
  const notify = useNotification();
  const { mutateAsync: updateDataset } = useUpdateDataset();

  // The name and description report a failed save in place; a tag, here.
  const save = (updates: Parameters<typeof updateDataset>[0]["updates"]) =>
    updateDataset({
      datasetId,
      resourceOwnerId: dataset?.org_id ?? "",
      updates,
    });
  const update = (updates: Parameters<typeof save>[0]) =>
    void save(updates).catch(() => notify("Couldn't save the change", "error"));

  const goToDatasetsByTag = (tag: string) => {
    const queryParams = new URLSearchParams();
    queryParams.set("query", tag);
    goto.search(queryParams);
  };

  return (
    <EntityHeader
      type="dataset"
      loading={datasetQuery.isLoading}
      name={
        dataset ? (
          <InlineEdit
            value={dataset.name ?? ""}
            onSave={(name) => save({ name })}
            required
            maxLength={120}
            placeholder="Untitled dataset"
            textStyle="title"
            ariaLabel="dataset name"
          />
        ) : (
          <EntityName>{datasetId}</EntityName>
        )
      }
      description={
        dataset && {
          value: dataset.description,
          canEdit: true,
          onSave: (description) => save({ description }),
        }
      }
      tags={
        dataset && {
          value: dataset.tags,
          canAdd: true,
          onAdd: (tag) => update({ metadata_changeset: { put_tags: [tag] } }),
          onClick: goToDatasetsByTag,
        }
      }
      meta={dataset ? datasetFacts(dataset) : []}
    />
  );
};

/** Who created it and when, and its device when it has one. */
function datasetFacts(dataset: DatasetRecord): EntityHeaderFact[] {
  const facts = auditFacts({
    created: dataset.created,
    createdBy: dataset.created_by,
  });
  if (dataset.device_id) {
    facts.push({
      key: "device",
      icon: <Bot />,
      value: <DeviceLink deviceId={dataset.device_id} />,
    });
  }
  return facts;
}
